import { formatDelta, formatDeltaPct, formatExact, formatValue } from "../columns";

/* Headline order, left to right. Only summable metrics appear: a total is shown
   only where it is an exact sum of the rows on screen.

   GP Margin is deliberately absent. It is calculated in dbt from period totals at
   billing-code grain and is covered by a dbt test; deriving a blended margin here
   would put a number on screen that Finance's own check would contradict. The
   per-row margin columns in the table carry the authoritative figures. */
const HEADLINE_METRICS = ["revenue", "gp", "cons_invoiced"];

const ACCENTS = {
  revenue: "#144677",
  gp: "#1a5691",
  cons_invoiced: "#6d28d9",
};

function attainmentTone(pct) {
  if (pct === null) return { bar: "#cbd5e1", text: "text-gray-400" };
  if (pct >= 100) return { bar: "#10b981", text: "text-emerald-600" };
  if (pct >= 95) return { bar: "#f59e0b", text: "text-amber-600" };
  return { bar: "#ef4444", text: "text-red-600" };
}

function ComparisonRow({ label, value, pct, unit }) {
  if (value === null || value === undefined) return null;
  const tone = value > 0 ? "text-emerald-600" : value < 0 ? "text-red-600" : "text-gray-400";
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-[11px] font-medium uppercase tracking-wider text-gray-400">
        {label}
      </span>
      <span className={`text-xs font-bold tabular-nums ${tone}`}>
        {formatDelta(value, unit)}
        {pct !== null && pct !== undefined && (
          <span className="ml-1 font-semibold opacity-70">({formatDeltaPct(pct)})</span>
        )}
      </span>
    </div>
  );
}


function BudgetRemainder({ actual, budget, unit }) {
  if (budget === null || budget === undefined || Number(budget) === 0) return null;

  const actualValue = actual ?? 0;
  const pct = (actualValue / budget) * 100;
  const tone = attainmentTone(pct);
  const shortfall = budget - actualValue;
  const met = shortfall <= 0;

  return (
    <div className="mt-3">
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="text-[11px] font-medium uppercase tracking-wider text-gray-400">
          {met ? "Budget exceeded by" : "Remaining to budget"}
        </span>
        <span
          className={`text-sm font-bold tabular-nums ${tone.text}`}
          title={formatExact(met ? -shortfall : shortfall, unit)}
        >
          {met ? formatDelta(-shortfall, unit) : formatValue(shortfall, unit)}
        </span>
      </div>

      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100"
        role="meter"
        aria-valuenow={Number(pct.toFixed(2))}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Budget attainment — ${pct.toFixed(2)} percent of target`}
      >
        <div
          className="h-full rounded-full transition-[width] duration-500 ease-out motion-reduce:transition-none"
          style={{ width: `${Math.min(Math.max(pct, 0), 100)}%`, background: tone.bar }}
        />
      </div>

      <div className="mt-1 flex items-baseline justify-between gap-2 text-[11px] text-gray-400">
        <span>of {formatValue(budget, unit)} budget</span>
        <span className="font-medium tabular-nums">
          {Math.abs(100 - pct).toFixed(2)}% {met ? "over" : "to go"}
        </span>
      </div>
    </div>
  );
}

function KpiCard({ metric, values }) {
  const accent = ACCENTS[metric.key] ?? "#144677";
  const actual = values.actual;
  const hasBudget = values.budget !== null && values.budget !== undefined && Number(values.budget) !== 0;
  const displayActual = actual ?? (hasBudget ? 0 : actual);

  return (
    <div
      className="rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-sm"
      style={{ borderTop: `3px solid ${accent}` }}
    >
      <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
        {metric.label}
      </p>
      <p
        className="mt-1.5 text-2xl font-black tabular-nums text-gray-900"
        title={formatExact(displayActual, metric.unit)}
      >
        {formatValue(displayActual, metric.unit)}
      </p>

      <BudgetRemainder actual={actual} budget={values.budget} unit={metric.unit} />

      <div className="mt-2.5">
        <ComparisonRow
          label="vs PY"
          value={values.var_py}
          pct={values.var_py_pct ?? null}
          unit={metric.unit}
        />
      </div>
    </div>
  );
}

export default function KpiStrip({ totals, metrics }) {
  const cards = HEADLINE_METRICS.map((key) => ({
    metric: (metrics ?? []).find((m) => m.key === key),
    values: totals?.[key],
  })).filter(({ metric, values }) => metric && values && Object.keys(values).length > 0);

  if (!cards.length) return null;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {cards.map(({ metric, values }) => (
        <KpiCard key={metric.key} metric={metric} values={values} />
      ))}
    </div>
  );
}
